import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { isBefore } from 'date-fns';
import { defaultTo, includes, pathOr, toLower } from 'ramda';
import { isNilOrEmpty } from 'ramda-extension';
import { useSelector } from 'react-redux';

import { getLabelMessage } from '@gef-ui/localization/messages';

import { codelistProvider, ComponentLoader } from 'components';
import OutputTextField from 'components/atoms/GefMui/OutputTextField';
import SectionHeader from 'components/atoms/GefMui/SectionHeader';
import { useIgnoreErrorsWhen } from 'components/molecules/ComponentLoader/hooks';
import { codelists } from 'constants/codelists';
import { EMPTY_VALUES } from 'constants/general';
import fetch from 'features/common/fetch';
import taskDetail from 'features/views/taskDetail';
import user from 'features/views/user';
import { equalOrThreeDashes, fromIsoDate, getCodelistPropById, number } from 'utils';

const { urls } = fetch.constants;

export const TaskGrid = ({ subtypes, onReload }) => {
	const detail = useSelector(taskDetail.selectors.getDetail);
	const currentUserLogin = useSelector(user.selectors.getUser);

	const { metadata = {}, solver = {}, read, loggedUserQueueMember } = detail;
	const { common = {} } = metadata;
	const desctiptionSubtype = getCodelistPropById(detail.taskSubtypeId, subtypes);

	const generateSolver = (solverLogin, read, loggedUserQueueMember) => {
		if (isNilOrEmpty(solverLogin)) {
			return loggedUserQueueMember && !read
				? toLower(defaultTo(EMPTY_VALUES.STRING, currentUserLogin))
				: EMPTY_VALUES.STRING;
		} else {
			return solverLogin;
		}
	};

	const fields = [
		{ label: 'detail:taskGridTypeOfActivity', value: detail.requestAction },
		{ label: 'detail:taskGridId', value: detail.id },
		{ label: 'detail:taskGridSubtype', value: desctiptionSubtype },
		{ label: 'detail:taskGridInvestigator', value: generateSolver(solver.login, read, loggedUserQueueMember) },
		{ label: 'detail:taskGridCreateDate', value: fromIsoDate(detail.dateCreated) },
		{ label: 'detail:taskGridCautionDate', value: fromIsoDate(detail.notificationDate) },
		{ label: 'detail:taskGridAssignOn', value: detail.queueGroup },
		{ label: 'detail:taskGridBonita', value: number(detail.caseValue) },
	];

	const deadlineDateWarning =
		(isBefore(new Date(detail.deadlineDate), new Date(detail.finishDate)) && detail.statusId === 3) ||
		(detail.statusId !== 3 && isBefore(new Date(detail.deadlineDate), new Date()));

	const feedbackMetada = pathOr({}, ['metadata', 'feedback'], detail);

	const ignoreErrorsForTaskDetailUserHasNoAccess = useIgnoreErrorsWhen({
		fetchStorePath: urls.taskDetail,
		attributePath: ['metaData', 'parsedContent', 'status'],
		value: 403,
	});

	return (
		<ComponentLoader
			onReload={onReload}
			fetchStorePath={urls.taskDetail}
			ignoreErrors={ignoreErrorsForTaskDetailUserHasNoAccess}
		>
			<Paper sx={{ p: 3, borderRadius: 2 }}>
				<Stack spacing={2}>
					<SectionHeader text={getLabelMessage('detail:taskGridSubheader')} />
					<Grid container>
						<Grid size={12}>
							<Grid container spacing={2}>
								{fields.map((item, i) => (
									<Grid key={i} size={4}>
										<OutputTextField
											size="small"
											id={getLabelMessage(item.label)}
											label={getLabelMessage(item.label)}
											fullWidth
											value={equalOrThreeDashes(item.value)}
											slotProps={{
												inputLabel: {
													shrink: !!equalOrThreeDashes(item.value),
												},
											}}
										/>
									</Grid>
								))}
								<Grid size={4}>
									<Box
										sx={{
											'& #taskGridDeadlineDate': {
												color: deadlineDateWarning ? 'red' : 'text.primary',
											},
										}}
									>
										<OutputTextField
											size="small"
											id="taskGridDeadlineDate"
											label={getLabelMessage('detail:taskGridDeadlineDate')}
											fullWidth
											value={equalOrThreeDashes(fromIsoDate(detail?.deadlineDate) || '')}
											slotProps={{
												inputLabel: {
													shrink: !!equalOrThreeDashes(detail?.deadlineDate),
												},
											}}
										/>
									</Box>
								</Grid>
								{detail.statusId === 3 && (
									<Grid size={4}>
										<OutputTextField
											size="small"
											id="taskGridSolvedData"
											label={getLabelMessage('detail:taskGridSolvedData')}
											fullWidth
											value={fromIsoDate(detail?.finishDate)}
											slotProps={{
												inputLabel: {
													shrink: !!detail?.finishDate,
												},
											}}
										/>
									</Grid>
								)}

								{includes(detail.taskTypeId, [12, 53]) && detail.finishTypeId === 83 && (
									<>
										{feedbackMetada.policyNo && (
											<Grid size={4}>
												<OutputTextField
													size="small"
													id="taskGridNewPS"
													label={getLabelMessage('detail:taskGridNewPS')}
													fullWidth
													value={equalOrThreeDashes(feedbackMetada?.policyNo)}
													slotProps={{
														inputLabel: {
															shrink:
																isNilOrEmpty(metadata) || !!feedbackMetada?.policyNo,
														},
													}}
												/>
											</Grid>
										)}
									</>
								)}

								{includes(detail.taskTypeId, [12, 53]) && (
									<>
										<Grid size={4}>
											<OutputTextField
												size="small"
												id="taskGridBirthDate"
												label={getLabelMessage('detail:taskGridBirthDate')}
												fullWidth
												value={equalOrThreeDashes(
													common.birthDate && fromIsoDate(common.birthDate),
												)}
												slotProps={{
													inputLabel: {
														shrink: isNilOrEmpty(common) || !!common?.birthDate,
													},
												}}
											/>
										</Grid>
									</>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Stack>
			</Paper>
		</ComponentLoader>
	);
};

TaskGrid.propTypes = {
	onReload: PropTypes.func,
	subtypes: PropTypes.array,
};

export default codelistProvider(codelists.taskSubtypes, 'subtypes')(TaskGrid);
